import { FormEvent, useCallback } from "react"

import { useForm } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { useTranslation } from "react-i18next"
import { RichTextEditor, useRichTextEditorContext } from "@mantine/tiptap"
import { DsButtonPrimary, DsPopover, DsTextInputBase } from "@raisesistemas/ds"

import { IconPlayCircle } from "../../../icons"
import { isVideoURL } from "../../../utils/isVideoURL"
import { isYouTubeURL } from "../../../utils/isYouTubeURL"
import { isVimeoVideoURL } from "../../../utils/isVimeoVideoURL"
import { generateVimeoUrl } from "../../../utils/generateVimeoUrl"

import { useStyles } from "./EditorVideoControl.style"

export const EditorVideoControl = () => {
  const { classes } = useStyles()
  const { editor } = useRichTextEditorContext()
  const [opened, handlers] = useDisclosure(false)
  const { t } = useTranslation()
  const form = useForm({
    initialValues: {
      url: ""
    },
    validate: {
      url: value => {
        if (value.length === 0) return t("validation.required_field")
        if (!isVideoURL(value)) return t("validation.invalid_url")
        return null
      }
    }
  })
  const handleClose = () => {
    handlers.close()
    form.reset()
  }

  const addVideo = useCallback(() => {
    const isYouTube = isYouTubeURL(form.values.url)
    const { url } = form.values
    let src = url

    if (isVimeoVideoURL(url)) src = generateVimeoUrl(url)

    if (src !== "") {
      const method = isYouTube ? "setYoutubeVideo" : "setIframe"
      editor.chain().focus()[method]({ src }).run()
      handleClose()
    }
  }, [editor, handleClose, form.values.url])

  return (
    <DsPopover
      width={300}
      shadow="md"
      withinPortal
      zIndex={10000}
      offset={-44}
      opened={opened}
      onClose={handleClose}
      targetChildren={
        <RichTextEditor.Control
          aria-label="Add video"
          title="Add video"
          onClick={handlers.open}
        >
          <IconPlayCircle size={16} />
        </RichTextEditor.Control>
      }
      dropdownChildren={
        <div className={classes.linkEditor}>
          <DsTextInputBase
            classNames={{ input: classes.linkEditorInput }}
            placeholder="YouTube/Vimeo URL"
            type="url"
            {...form.getInputProps("url")}
          />
          <DsButtonPrimary
            className={classes.linkEditorSave}
            onClick={(e: unknown) =>
              form.onSubmit(addVideo)(e as FormEvent<HTMLFormElement>)
            }
          >
            {t("button.save")}
          </DsButtonPrimary>
        </div>
      }
    />
  )
}
